/**
 * MidiMonitor — small popover that shows the most recent MIDI traffic
 * coming into the synth. Useful for debugging "is my controller even
 * connected?", "what CC does this knob send?", and "why isn't my mod
 * wheel doing anything?".
 *
 * Always-on subscription keeps a rolling ring buffer (RING_SIZE) of the
 * latest messages. When the popover is closed we just append to the
 * buffer; when it's open we also render. Closing again stops rendering
 * but the buffer keeps filling so re-opening immediately shows current
 * traffic.
 */

import type { MidiEngine } from '@ac-slayer/synth-midi';
import type { MidiMessage } from '@ac-slayer/synth-midi';

/** How many messages to retain. ~20 fits the popover height comfortably
 *  and is plenty for "what did my controller just send?" debugging. */
const RING_SIZE = 20;

interface Entry {
  readonly message: MidiMessage;
  readonly inputId: string;
  readonly timestamp: number;
}

export interface MidiMonitorOptions {
  readonly engine: MidiEngine;
}

export class MidiMonitor {
  /** Wrapper element — caller appends this somewhere in the topbar. */
  readonly element: HTMLElement;

  private readonly engine: MidiEngine;
  private readonly button: HTMLButtonElement;
  private readonly popover: HTMLElement;
  private readonly list: HTMLElement;
  private readonly emptyState: HTMLElement;

  private buffer: Entry[] = [];
  private isOpen = false;
  private readonly unsubscribe: () => void;
  private readonly outsideClick = (ev: MouseEvent): void => {
    if (!this.isOpen) return;
    if (ev.target instanceof Node && this.element.contains(ev.target)) return;
    this.close();
  };

  constructor(opts: MidiMonitorOptions) {
    this.engine = opts.engine;

    this.element = document.createElement('div');
    this.element.className = 'midi-monitor';

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'btn btn-sm midi-monitor-button';
    this.button.textContent = 'Monitor';
    this.button.title = 'Show recent MIDI messages';
    this.button.setAttribute('aria-haspopup', 'true');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.addEventListener('click', () => this.toggle());
    this.element.appendChild(this.button);

    this.popover = document.createElement('div');
    this.popover.className = 'midi-monitor-popover';
    this.popover.hidden = true;
    this.popover.setAttribute('role', 'log');
    this.popover.setAttribute('aria-live', 'polite');

    const header = document.createElement('div');
    header.className = 'midi-monitor-header';
    const title = document.createElement('span');
    title.className = 'midi-monitor-title';
    title.textContent = 'MIDI Monitor';
    header.appendChild(title);
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'btn btn-sm';
    clearBtn.textContent = 'Clear';
    clearBtn.addEventListener('click', () => {
      this.buffer = [];
      this.render();
    });
    header.appendChild(clearBtn);
    this.popover.appendChild(header);

    this.list = document.createElement('ol');
    this.list.className = 'midi-monitor-list mono';
    this.popover.appendChild(this.list);

    this.emptyState = document.createElement('div');
    this.emptyState.className = 'midi-monitor-empty label';
    this.emptyState.textContent = 'Waiting for MIDI…';
    this.popover.appendChild(this.emptyState);

    this.element.appendChild(this.popover);

    this.unsubscribe = this.engine.onMessage((message, inputId) => {
      this.buffer.push({ message, inputId, timestamp: Date.now() });
      if (this.buffer.length > RING_SIZE) {
        this.buffer.splice(0, this.buffer.length - RING_SIZE);
      }
      if (this.isOpen) this.render();
    });
  }

  dispose(): void {
    this.unsubscribe();
    document.removeEventListener('mousedown', this.outsideClick);
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private open(): void {
    this.isOpen = true;
    this.popover.hidden = false;
    this.button.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    this.render();
    // Defer attaching the outside-click listener until after the current
    // event loop tick so the click that opened the popover doesn't
    // immediately close it.
    setTimeout(() => {
      document.addEventListener('mousedown', this.outsideClick);
    }, 0);
  }

  private close(): void {
    this.isOpen = false;
    this.popover.hidden = true;
    this.button.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', this.outsideClick);
  }

  private render(): void {
    this.list.innerHTML = '';
    if (this.buffer.length === 0) {
      this.emptyState.hidden = false;
      this.list.hidden = true;
      return;
    }
    this.emptyState.hidden = true;
    this.list.hidden = false;
    // Newest first so the user sees the most recent activity at the top.
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const entry = this.buffer[i]!;
      const li = document.createElement('li');
      li.className = 'midi-monitor-row';
      li.textContent = formatEntry(entry);
      this.list.appendChild(li);
    }
  }
}

function formatEntry(entry: Entry): string {
  const m = entry.message;
  const ch = `ch${m.channel + 1}`;
  switch (m.type) {
    case 'noteOn':
      return `${ch}  Note On   ${noteName(m.note)} v=${(m.velocity * 127) | 0}`;
    case 'noteOff':
      return `${ch}  Note Off  ${noteName(m.note)}`;
    case 'cc':
      return `${ch}  CC ${pad3(m.controller)}    ${pad3(m.value)}`;
    case 'pitchBend':
      return `${ch}  Pitch     ${m.value.toFixed(3)}`;
    case 'allNotesOff':
      return `${ch}  All Notes Off`;
  }
}

function noteName(midi: number): string {
  const NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octave = Math.floor(midi / 12) - 1;
  const name = NAMES[midi % 12]!;
  return `${name}${octave}`;
}

function pad3(n: number): string {
  return n.toString().padStart(3, ' ');
}
